import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './styles.scss';
import { DateCarousel } from 'widget/event_components/Ticket/components/Datetime/components/DateCarousel';
import Divider from '@mui/material/Divider';
import { ColorDescription } from 'widget/event_components/Ticket/components/Datetime/ColorDescription';
import moment from 'moment';
import Periods from 'widget/event_components/Ticket/components/Datetime/components/Periods';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { List } from 'immutable';

class DateTimeEntriesFactory {
  constructor(occurrences) {
    this.occurrences = occurrences;
  }

  sortTimes(timeEntries) {
    return timeEntries.sort((a, b) => {
      const firstIterateDate = a.get('start_date');
      const secondIterateDate = b.get('start_date');

      if (moment(firstIterateDate).isAfter(secondIterateDate)) return 1;
      if (moment(firstIterateDate).isBefore(secondIterateDate)) return -1;

      return 0;
    });
  }

  timeEntriesIterator(timeEntries) {
    return this.sortTimes(timeEntries);
  }

  occurrencesIterator() {
    return this.sortTimes(this.occurrences).map((occurrence) => ({
      start: moment(occurrence.get('start_date')).tz(occurrence.get('timezone')),
      end: moment(occurrence.get('end_date')).tz(occurrence.get('timezone')),
      id: occurrence.get('id'),
      timeEntries: this.timeEntriesIterator(occurrence.get('time_entries')),
    }));
  }

  getEntries() {
    return this.occurrencesIterator();
  }
}

const BackToDatePlate = ({ occurrenceDate }) => (
  <div className={styles.backToDateContainer}>
    <div className={styles.dateInPlate}>
      <CalendarMonthIcon />
      {occurrenceDate.format('ddd, D MMMM YYYY')}
    </div>
  </div>
);

const MultiSessionSelector = ({ event, settings, onSelectAccessType, selectedAccessType }) => {
  const isMultiSession = event.get('multi_session_event');

  if (!isMultiSession) return null;

  return (
    <div className={styles.multiSessionWrapper}>
      <div className={styles.multiSessionPeriod}>
        <div className={styles.multiSessionTitle}>{settings.get('multi_session_label_text') || 'Event Access'}</div>
        <div className={styles.multiSessionLine}>
          <div
            className={`${styles.multiSessionItem} ${selectedAccessType === 'single' ? styles.multiSessionSelected : ''}`}
            onClick={() => onSelectAccessType('single')}
          >
            {settings.get('single_session_selection_text') || 'Single Session'}
          </div>
          <div
            className={`${styles.multiSessionItem} ${selectedAccessType === 'all' ? styles.multiSessionSelected : ''}`}
            onClick={() => onSelectAccessType('all')}
          >
            {settings.get('multi_session_selection_text') || 'All Sessions'}
          </div>
        </div>
      </div>
      <Divider className={styles.divider} variant="fullWidth" />
    </div>
  );
};

export const CommonView = ({ event, occurrences = List(), setTickets, setEvent, settings, occurrenceDate, onClickBack, setOccurrencesDate }) => {
  const [selectedOccurrence, setSelectedOccurrence] = useState();
  const [selectedAccessType, setSelectedAccessType] = useState('single');

  useEffect(() => {
    if (occurrenceDate) {
      occurrences.forEach((occurrence) => {
        const choiceDate = moment(occurrenceDate.tz(occurrence.get('timezone')));
        const eventDate = moment(moment(occurrence.get('start_date')).tz(occurrence.get('timezone')));

        if (choiceDate.isSame(eventDate, 'day')) {
          setSelectedOccurrence(occurrence.get('id'));
        }
      });
    } else if (occurrences.first && occurrences.first()) {
      setSelectedOccurrence(occurrences.first().get('id'));
    }
  }, [occurrences, occurrenceDate]);

  useEffect(() => {
    if (selectedAccessType === 'all') {
      // Collect all tickets from all occurrences and time entries
      const parentOccurrence = occurrences.find((e) => e.get('multi_session_event') && e.get('is_series_parent')).set('multi_session_access', true);
      const allTickets = parentOccurrence.get('tickets');
      setTickets(allTickets);
    }
  }, [selectedAccessType, occurrences, setTickets]);

  const entries = useMemo(
    () => new DateTimeEntriesFactory(occurrences.filter((o) => !o.get('is_series_parent'))).getEntries(),
    [occurrences, occurrenceDate]
  );

  const handleDateOnClick = useCallback((id) => {
    setSelectedOccurrence(id);
  }, []);

  const handleAccessTypeSelect = useCallback(
    (accessType) => {
      setSelectedAccessType(accessType);

      if (accessType === 'all') {
        // Set the event with multi_session_access flag
        setEvent(occurrences.find((e) => e.get('multi_session_event') && e.get('is_series_parent')).set('multi_session_access', true));
      }
    },
    [event, setEvent]
  );

  const currentEntries = useMemo(() => entries.find((record) => record.id === selectedOccurrence), [selectedOccurrence]);

  return (
    <div className={styles.wrapper}>
      <MultiSessionSelector event={event} settings={settings} onSelectAccessType={handleAccessTypeSelect} selectedAccessType={selectedAccessType} />
      {selectedAccessType === 'single' && (
        <>
          {occurrenceDate ? (
            <BackToDatePlate occurrenceDate={occurrenceDate} onClickBack={onClickBack} />
          ) : (
            <DateCarousel dateTimeEntries={entries} selectedOccurrence={selectedOccurrence} onClickOccurrence={handleDateOnClick} />
          )}
          <Divider className={styles.divider} variant="fullWidth" />
          <Periods
            setOccurrencesDate={setOccurrencesDate}
            event={event}
            setEvent={setEvent}
            setTickets={setTickets}
            selectedOccurrence={currentEntries}
            settings={settings}
            key={event.get('id')}
          />
        </>
      )}
      <ColorDescription settings={settings} className={styles.colorDescriptionWrapper} />
    </div>
  );
};
